import { Navigate, useParams } from 'react-router-dom';

import { Spinner } from '@/components/generic';
import { shouldEnforceFirebaseMfa } from '@/configuration/ssoProviders';
import { useAuth } from '@/contexts';
import { PERMISSION_TYPE, PRIVATE_PAGE_ROUTES, PUBLIC_PAGE_ROUTES } from '@/enums';
import { getDefaultPrivateRoute } from '@/helpers';
import { PrivateRoute } from '@/types';

type ProtectedRouteProps = {
  route: PrivateRoute;
};

export const ProtectedRoute = ({ route }: ProtectedRouteProps) => {
  const {
    user,
    hasPermission,
    hasAllPermissions,
    hasAllOrganizationPermissions,
    currentUserData,
    isAuthLoading,
    signInProvider
  } = useAuth();
  const { eventId } = useParams();
  const fallbackPath = getDefaultPrivateRoute(currentUserData);

  if (isAuthLoading || (user && signInProvider === null)) {
    return (
      <div className='flex h-screen items-center justify-center'>
        <Spinner />
      </div>
    );
  }

  if (!user) {
    return <Navigate to={PUBLIC_PAGE_ROUTES.LOGIN} replace />;
  }

  if (currentUserData?.isMfaForced && shouldEnforceFirebaseMfa(signInProvider)) {
    return <Navigate to={PRIVATE_PAGE_ROUTES.MFA_SETUP} replace />;
  }

  // An uploader belongs to the event's organization but may only open the ceremonies it was
  // invited to; the API refuses the rest, this keeps it off their pages too.
  const isUploader =
    hasPermission(PERMISSION_TYPE.EVENT_ACCESS) && !hasPermission(PERMISSION_TYPE.ORG_ACCESS);
  const canOpenEvent =
    !isUploader || !eventId || !!currentUserData?.events?.some((e) => String(e.id) === eventId);

  const hasAccess =
    hasAllPermissions(route.permissions ?? []) &&
    hasAllOrganizationPermissions(route.organizationPermissions ?? []) &&
    (!route.uploaderOnly || isUploader) &&
    canOpenEvent;

  if (!hasAccess) {
    return <Navigate to={fallbackPath} replace />;
  }

  return route.element;
};
